import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';

const COLORS = ['#22d3ee', '#818cf8', '#f59e0b', '#34d399', '#fb7185', '#a78bfa'];

/** Purpose: Perform get card sx for the analytics module. Why: Callers need a consistent result from this shared operation. */
const getCardSx = (theme) => ({
  p: 2.5,
  borderRadius: 2.5,
  border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.75 : 0.85)}`,
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(145deg, rgba(15,23,42,0.96) 0%, rgba(15,23,42,0.84) 100%)'
    : 'linear-gradient(145deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.94) 100%)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 16px 38px rgba(2,6,23,0.28)'
    : '0 8px 24px rgba(15,23,42,0.055)',
});

/** Purpose: Build stat card within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const StatCard = ({ title, value, subtitle, accent, icon }) => {
  const theme = useTheme();

  return (
  <Paper
    sx={{
      ...getCardSx(theme),
      minHeight: 142,
      position: 'relative',
      overflow: 'hidden',
      transition: 'transform 180ms ease, box-shadow 180ms ease',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '0 auto 0 0',
        width: 3,
        bgcolor: accent,
      },
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 20px 44px rgba(2,6,23,0.36)'
          : '0 16px 36px rgba(15,23,42,0.1)',
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'grid', placeItems: 'center', color: accent, bgcolor: alpha(accent, .1) }}>{icon}</Box>
    </Stack>
    <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-0.04em', lineHeight: 1.05 }}>
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, lineHeight: 1.45 }}>
      {subtitle}
    </Typography>
  </Paper>
  );
};

/** Purpose: Build empty chart within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const EmptyChart = ({ title = 'No activity yet' }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ height: 260, textAlign: 'center', px: 3 }}>
    <Box sx={{ width: 52, height: 52, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'text.secondary', mb: 1.5 }}><InsightsOutlinedIcon /></Box>
    <Typography fontWeight={750}>{title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: .5, maxWidth: 340 }}>Data will appear automatically when users start conversations in this environment.</Typography>
  </Stack>
);

/** Purpose: Perform has chart values for the analytics module. Why: Callers need a consistent result from this shared operation. */
const hasChartValues = (rows = []) => Array.isArray(rows) && rows.some((row) => Object.values(row || {}).some((value) => typeof value === 'number' && value > 0));

/** Purpose: Build custom flow tooltip within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const CustomFlowTooltip = ({ active, payload, label, theme }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload || {};
    const rate = data.completionRate ?? 0;
    const rateColor = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#f43f5e';

    return (
      <Paper
        sx={{
          p: 1.5,
          minWidth: 190,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.96)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.8 }}>
          {data.flowName || label}
        </Typography>
        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">Visits (Sessions):</Typography>
            <Typography variant="caption" sx={{ fontWeight: 750, color: '#22d3ee' }}>
              {(data.sessions || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">Unique Users:</Typography>
            <Typography variant="caption" sx={{ fontWeight: 750, color: '#818cf8' }}>
              {(data.uniqueUsers || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="caption" color="text.secondary">Completed:</Typography>
            <Typography variant="caption" sx={{ fontWeight: 750 }}>
              {(data.completedSessions || 0).toLocaleString()}
            </Typography>
          </Stack>
          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Completion Rate:</Typography>
            <Typography variant="caption" sx={{ fontWeight: 850, color: rateColor }}>
              {rate}%
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    );
  }
  return null;
};

/** Purpose: summarize bot usage and operational metrics. Why: workspace owners need one view to monitor trends across conversations and channels. */
export default function Analytics() {
  const theme = useTheme();
  const { selectedBot, selectedEnv } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flowLimit, setFlowLimit] = useState('5');
  const [flowViewMode, setFlowViewMode] = useState('both');
  const [filters, setFilters] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 29);

    return {
      startDate: start.toISOString().slice(0, 10),
      endDate: end.toISOString().slice(0, 10),
    };
  });
  const [headerHost, setHeaderHost] = useState(() => (
    typeof document !== 'undefined' ? document.getElementById('analytics-header-actions') : null
  ));

  useEffect(() => {
    const element = document.getElementById('analytics-header-actions');
    if (element) setHeaderHost(element);
  }, []);

  /** Purpose: Load fetch analytics for the analytics module. Why: The selected view needs current data to render or continue processing. */
  const fetchAnalytics = async () => {
    if (!selectedBot) {
      setAnalytics(null);
      setLoading(false);
      setError('Please select a bot first.');
      return;
    }

    if (!filters.startDate || !filters.endDate) {
      setError('Please select both from and to dates.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/api/v1/analytics/overview', {
        params: {
          botId: selectedBot,
          envName: selectedEnv || 'dev',
          startDate: filters.startDate,
          endDate: filters.endDate,
        },
      });
      setAnalytics(response.data);
    } catch (fetchError) {
      setAnalytics(null);
      setError(fetchError.response?.data?.error || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [selectedBot, selectedEnv]);

  /** Purpose: Transform channel chart data within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const channelChartData = useMemo(() => {
    const breakdown = analytics?.channelBreakdown || {};
    return Object.entries(breakdown).map(([name, value]) => ({ name, value }));
  }, [analytics]);

  /** Purpose: Transform status chart data within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const statusChartData = useMemo(() => {
    const breakdown = analytics?.statusBreakdown || {};
    return Object.entries(breakdown).map(([name, value]) => ({ name, value }));
  }, [analytics]);

  /** Purpose: Perform displayed top flows for the analytics module. Why: Callers need a consistent result from this shared operation. */
  const displayedTopFlows = useMemo(() => {
    const flows = Array.isArray(analytics?.topFlows) ? [...analytics.topFlows] : [];
    flows.sort((a, b) => {
      if (b.sessions !== a.sessions) return b.sessions - a.sessions;
      return (b.completionRate || 0) - (a.completionRate || 0);
    });
    if (flowLimit === '5') return flows.slice(0, 5);
    if (flowLimit === '10') return flows.slice(0, 10);
    return flows;
  }, [analytics?.topFlows, flowLimit]);

  /** Purpose: Resolve performance highlights for the analytics module. Why: The surrounding operation uses this value to make its decision. */
  const performanceHighlights = useMemo(() => {
    if (!analytics) {
      return [];
    }

    const items = [];

    if (analytics.insights?.summary) {
      items.push(analytics.insights.summary);
    }

    if (analytics.insights?.topChannel) {
      items.push(`${analytics.insights.topChannel.channel} was the top channel with ${analytics.insights.topChannel.count} conversations.`);
    }

    if (analytics.insights?.busiestDay) {
      items.push(`${analytics.insights.busiestDay.date} was the busiest day with ${analytics.insights.busiestDay.sessions} sessions.`);
    }

    if (analytics.insights?.bestFlow) {
      items.push(
        `${analytics.insights.bestFlow.flowName} was the best-performing flow with ${analytics.insights.bestFlow.sessions} sessions and a ${analytics.insights.bestFlow.completionRate}% completion rate.`
      );
    }

    if (analytics.insights?.topDropoffNode) {
      items.push(
        `The highest drop-off occurred at the ${analytics.insights.topDropoffNode.nodeLabel} node in ${analytics.insights.topDropoffNode.flowName}, where ${analytics.insights.topDropoffNode.dropoffCount} sessions stopped.`
      );
    }

    return items;
  }, [analytics]);

  /** Purpose: Transform node dropoff chart data within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const nodeDropoffChartData = useMemo(() => {
    return (analytics?.nodeDropoff || []).slice(0, 8).map((item) => ({
      ...item,
      label: item.nodeLabel?.length > 18 ? `${item.nodeLabel.slice(0, 18)}...` : item.nodeLabel,
    }));
  }, [analytics]);

  /** Purpose: Derive the card sx style for the analytics view. Why: Theme and state dependent styling should be calculated from current UI context. */
  const cardSx = useMemo(() => getCardSx(theme), [theme]);
  /** Purpose: Build chart paper sx within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const chartPaperSx = useMemo(() => ({ ...getCardSx(theme), minHeight: 390 }), [theme]);
  const chartGridColor = theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.16)' : 'rgba(148,163,184,0.24)';
  const chartTickColor = theme.palette.text.secondary;
  /** Purpose: Build recent date chip sx within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const recentDateChipSx = useMemo(() => ({
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
    fontWeight: 700,
    border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.34 : 0.22)}`,
  }), [theme]);
  /** Purpose: Build table head cell sx within the analytics module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const tableHeadCellSx = useMemo(() => ({
    bgcolor: theme.palette.mode === 'dark' ? alpha('#0f172a', 0.96) : alpha('#f8fafc', 0.96),
    color: theme.palette.text.secondary,
    fontWeight: 700,
    borderBottomColor: alpha(theme.palette.divider, 0.9),
  }), [theme]);

  const analyticsHeaderActions = headerHost ? createPortal(
    <>
      <TextField
        label="From date"
        type="date"
        value={filters.startDate}
        onChange={(event) => setFilters((current) => ({ ...current, startDate: event.target.value }))}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: filters.endDate }}
        size="small"
        sx={{ minWidth: { xs: 145, sm: 165 }, '& .MuiOutlinedInput-root': { bgcolor: alpha(theme.palette.background.paper, 0.7) } }}
      />
      <TextField
        label="To date"
        type="date"
        value={filters.endDate}
        onChange={(event) => setFilters((current) => ({ ...current, endDate: event.target.value }))}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: filters.startDate }}
        size="small"
        sx={{ minWidth: { xs: 145, sm: 165 }, '& .MuiOutlinedInput-root': { bgcolor: alpha(theme.palette.background.paper, 0.7) } }}
      />
      <Button
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshOutlinedIcon />}
        variant="contained"
        onClick={fetchAnalytics}
        disabled={loading}
        sx={{ px: 2, minHeight: 40, flexShrink: 0, whiteSpace: 'nowrap', borderRadius: 2, boxShadow: 'none' }}
      >
        Refresh
      </Button>
    </>,
    headerHost
  ) : null;

  if (loading) {
    return (
      <>
        {analyticsHeaderActions}
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 1680, mx: 'auto', width: '100%' }}>
      {analyticsHeaderActions}

      {error ? (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      {!analytics ? (
        <Paper sx={{ ...cardSx, p: 4 }}>
          <Typography>No analytics data available for this bot and date range.</Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            }}
          >
            <StatCard
              title="Total Users"
              value={analytics.overview.totalUsers}
              subtitle="Users in the selected range."
              accent="#22d3ee"
              icon={<PeopleAltOutlinedIcon fontSize="small" />}
            />
            <StatCard
              title="Total Sessions"
              value={analytics.overview.totalSessions}
              subtitle="Journeys started by users."
              accent="#818cf8"
              icon={<ForumOutlinedIcon fontSize="small" />}
            />
            <StatCard
              title="Visited Flows"
              value={analytics.overview.visitedFlows}
              subtitle="Distinct flows reached."
              accent="#f59e0b"
              icon={<AccountTreeOutlinedIcon fontSize="small" />}
            />
            <StatCard
              title="Messages"
              value={analytics.overview.totalMessages}
              subtitle={`${analytics.overview.avgMessagesPerSession} average per session.`}
              accent="#34d399"
              icon={<ChatBubbleOutlineOutlinedIcon fontSize="small" />}
            />
            <StatCard
              title="Completion Rate"
              value={`${analytics.overview.completionRate}%`}
              subtitle={`${analytics.overview.totalConversations} conversations • ${analytics.overview.activeChannels} channels.`}
              accent="#fb7185"
              icon={<TaskAltOutlinedIcon fontSize="small" />}
            />
          </Box>

          <Paper sx={{ ...cardSx, p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.25 }}>
              Performance Summary
            </Typography>
            <Stack direction="column" spacing={1}>
              {performanceHighlights.length ? performanceHighlights.map((item) => (
                <Typography key={item} variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {item}
                </Typography>
              )) : (
                <Typography variant="body2" color="text.secondary">
                  There is not enough activity in the selected range to summarize performance.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'minmax(0, 1.7fr) minmax(340px, 0.9fr)',
              },
            }}
          >
            <Paper sx={chartPaperSx}>
              <Typography variant="h6" gutterBottom>
                Usage Trend
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Trends for sessions, unique users, and messages within the selected date range.
              </Typography>
              {hasChartValues(analytics.timeline) ? <ResponsiveContainer width="100%" height={290}>
                <LineChart data={analytics.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                  <XAxis dataKey="date" tick={{ fill: chartTickColor, fontSize: 12 }} />
                  <YAxis tick={{ fill: chartTickColor, fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sessions" stroke="#22d3ee" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="users" stroke="#818cf8" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="messages" stroke="#f59e0b" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer> : <EmptyChart title="No usage recorded in this period" />}
            </Paper>

            <Paper sx={chartPaperSx}>
              <Typography variant="h6" gutterBottom>
                Channel Split
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Distribution of conversations across channels.
              </Typography>
              {hasChartValues(channelChartData) ? <ResponsiveContainer width="100%" height={290}>
                <PieChart>
                  <Pie data={channelChartData} dataKey="value" nameKey="name" outerRadius={108} innerRadius={56}>
                    {channelChartData.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer> : <EmptyChart title="No channel activity yet" />}
            </Paper>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'minmax(0, 1.35fr) minmax(340px, 0.95fr)',
              },
            }}
          >
            <Paper sx={{ ...chartPaperSx, p: 3, display: 'flex', flexDirection: 'column' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Top Flows
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        flowLimit === 'all'
                          ? `All (${displayedTopFlows.length})`
                          : `Top ${flowLimit}`
                      }
                      sx={{
                        fontSize: '0.72rem',
                        height: 22,
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                    Flows generating the most visits, unique users, and their completion rate.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {/* Filter: Top 5 / Top 10 / All */}
                  <ToggleButtonGroup
                    size="small"
                    value={flowLimit}
                    exclusive
                    onChange={(_, next) => next && setFlowLimit(next)}
                    sx={{
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      borderRadius: 2,
                      p: 0.3,
                      border: `1px solid ${theme.palette.divider}`,
                      '& .MuiToggleButton-root': {
                        px: 1.25,
                        py: 0.35,
                        fontSize: '0.74rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        border: 'none',
                        borderRadius: '6px !important',
                        '&.Mui-selected': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(34, 211, 238, 0.18)' : 'rgba(34, 211, 238, 0.14)',
                          color: '#0891b2',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="5">Top 5</ToggleButton>
                    <ToggleButton value="10">Top 10</ToggleButton>
                    <ToggleButton value="all">All Flows</ToggleButton>
                  </ToggleButtonGroup>

                  {/* View: Both / Chart / Table */}
                  <ToggleButtonGroup
                    size="small"
                    value={flowViewMode}
                    exclusive
                    onChange={(_, next) => next && setFlowViewMode(next)}
                    sx={{
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      borderRadius: 2,
                      p: 0.3,
                      border: `1px solid ${theme.palette.divider}`,
                      '& .MuiToggleButton-root': {
                        px: 1.2,
                        py: 0.35,
                        fontSize: '0.74rem',
                        fontWeight: 650,
                        textTransform: 'none',
                        border: 'none',
                        borderRadius: '6px !important',
                        '&.Mui-selected': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.22)' : 'rgba(129, 140, 248, 0.16)',
                          color: '#6366f1',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="both">Both</ToggleButton>
                    <ToggleButton value="chart">Chart</ToggleButton>
                    <ToggleButton value="table">Table</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>

              {hasChartValues(displayedTopFlows) ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Chart (rendered when both or chart) */}
                  {(flowViewMode === 'both' || flowViewMode === 'chart') && (
                    <Box sx={{ width: '100%', height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={displayedTopFlows} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                          <XAxis
                            dataKey="flowName"
                            tick={{ fill: chartTickColor, fontSize: 11 }}
                            interval={0}
                            tickFormatter={(val) => (val && val.length > 14 ? `${val.slice(0, 14)}…` : val)}
                          />
                          <YAxis
                            yAxisId="left"
                            tick={{ fill: chartTickColor, fontSize: 11 }}
                            allowDecimals={false}
                          />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={[0, 100]}
                            unit="%"
                            tick={{ fill: chartTickColor, fontSize: 11 }}
                          />
                          <Tooltip content={<CustomFlowTooltip theme={theme} />} />
                          <Legend wrapperStyle={{ paddingTop: 6, fontSize: '0.78rem' }} />
                          <Bar
                            yAxisId="left"
                            dataKey="sessions"
                            name="Visits (Sessions)"
                            fill="#22d3ee"
                            radius={[6, 6, 0, 0]}
                            maxBarSize={48}
                          />
                          <Bar
                            yAxisId="left"
                            dataKey="uniqueUsers"
                            name="Unique Users"
                            fill="#818cf8"
                            radius={[6, 6, 0, 0]}
                            maxBarSize={48}
                          />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="completionRate"
                            name="Completion Rate (%)"
                            stroke="#10b981"
                            strokeWidth={3}
                            dot={{ fill: '#10b981', r: 4, strokeWidth: 1.5, stroke: '#fff' }}
                            activeDot={{ r: 6 }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  {/* Table (rendered when both or table) */}
                  {(flowViewMode === 'both' || flowViewMode === 'table') && (
                    <TableContainer sx={{ maxHeight: 320, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...tableHeadCellSx, width: 44, textAlign: 'center' }}>#</TableCell>
                            <TableCell sx={tableHeadCellSx}>Flow Name</TableCell>
                            <TableCell sx={tableHeadCellSx} align="right">Visits</TableCell>
                            <TableCell sx={tableHeadCellSx} align="right">Unique Users</TableCell>
                            <TableCell sx={tableHeadCellSx} align="right">Completed</TableCell>
                            <TableCell sx={{ ...tableHeadCellSx, minWidth: 150 }} align="right">Completion Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayedTopFlows.map((flow, index) => {
                            const rate = flow.completionRate ?? 0;
                            const rateColor = rate >= 70 ? '#10b981' : rate >= 40 ? '#f59e0b' : '#f43f5e';
                            const rateBg = rate >= 70
                              ? 'rgba(16, 185, 129, 0.12)'
                              : rate >= 40
                                ? 'rgba(245, 158, 11, 0.12)'
                                : 'rgba(244, 63, 94, 0.12)';

                            return (
                              <TableRow key={flow.flowId || index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align="center">
                                  <Box
                                    sx={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: '50%',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.7rem',
                                      fontWeight: 800,
                                      bgcolor: index === 0
                                        ? 'rgba(245, 158, 11, 0.2)'
                                        : index === 1
                                          ? 'rgba(148, 163, 184, 0.2)'
                                          : index === 2
                                            ? 'rgba(217, 119, 6, 0.2)'
                                            : 'action.hover',
                                      color: index === 0
                                        ? '#d97706'
                                        : index === 1
                                          ? '#64748b'
                                          : index === 2
                                            ? '#b45309'
                                            : 'text.secondary',
                                    }}
                                  >
                                    {index + 1}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {flow.flowName}
                                  </Typography>
                                  {flow.flowId && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', fontFamily: 'monospace' }}>
                                      {flow.flowId}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 750 }}>
                                    {Number(flow.sessions || 0).toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {Number(flow.uniqueUsers || 0).toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="text.secondary">
                                    {Number(flow.completedSessions || 0).toLocaleString()}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                    <Box sx={{ width: 56, mr: 0.5 }}>
                                      <LinearProgress
                                        variant="determinate"
                                        value={Math.min(100, Math.max(0, rate))}
                                        sx={{
                                          height: 6,
                                          borderRadius: 3,
                                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: rateColor,
                                            borderRadius: 3,
                                          },
                                        }}
                                      />
                                    </Box>
                                    <Chip
                                      size="small"
                                      label={`${rate}%`}
                                      sx={{
                                        height: 22,
                                        fontSize: '0.72rem',
                                        fontWeight: 800,
                                        bgcolor: rateBg,
                                        color: rateColor,
                                        minWidth: 46,
                                      }}
                                    />
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              ) : (
                <EmptyChart title="No flow sessions yet" />
              )}
            </Paper>

            <Paper sx={chartPaperSx}>
              <Typography variant="h6" gutterBottom>
                Conversation Status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Breakdown of open, assigned, pending, and resolved conversations.
              </Typography>
              {hasChartValues(statusChartData) ? <ResponsiveContainer width="100%" height={290}>
                <BarChart data={statusChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                  <XAxis dataKey="name" tick={{ fill: chartTickColor, fontSize: 12 }} />
                  <YAxis tick={{ fill: chartTickColor, fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {statusChartData.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer> : <EmptyChart title="No conversation statuses yet" />}
            </Paper>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'minmax(0, 1.25fr) minmax(360px, 1fr)',
              },
            }}
          >
            <Paper sx={chartPaperSx}>
              <Typography variant="h6" gutterBottom>
                Node Drop-off Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Identifies the nodes where users most often stop or cannot continue their journey.
              </Typography>
              {hasChartValues(nodeDropoffChartData) ? <ResponsiveContainer width="100%" height={290}>
                <BarChart data={nodeDropoffChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                  <XAxis dataKey="label" tick={{ fill: chartTickColor, fontSize: 11 }} />
                  <YAxis tick={{ fill: chartTickColor, fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="dropoffCount" fill="#fb7185" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="visits" fill="#22d3ee" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer> : <EmptyChart title="No drop-off data yet" />}
            </Paper>

            <Paper sx={{ ...cardSx, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Drop-off Nodes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use this table to identify flows and nodes that need optimization.
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Flow</TableCell>
                      <TableCell sx={tableHeadCellSx}>Node</TableCell>
                      <TableCell sx={tableHeadCellSx}>Type</TableCell>
                      <TableCell sx={tableHeadCellSx} align="right">Visits</TableCell>
                      <TableCell sx={tableHeadCellSx} align="right">Drop-off</TableCell>
                      <TableCell sx={tableHeadCellSx} align="right">Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.nodeDropoff?.length ? analytics.nodeDropoff.map((item) => (
                      <TableRow key={`${item.flowId}-${item.nodeId}`}>
                        <TableCell>{item.flowName}</TableCell>
                        <TableCell>{item.nodeLabel}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{String(item.nodeType || 'unknown').replaceAll('_', ' ')}</TableCell>
                        <TableCell align="right">{item.visits}</TableCell>
                        <TableCell align="right">{item.dropoffCount}</TableCell>
                        <TableCell align="right">{item.dropoffRate}%</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={6}>No node-level drop-off data is available yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Paper sx={{ ...cardSx, p: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Recent Active Sessions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review active flows and the users with the latest session activity.
                </Typography>
              </Box>
              <Chip
                label={`${filters.startDate} to ${filters.endDate}`}
                sx={recentDateChipSx}
              />
            </Stack>
            <Divider sx={{ mb: 2, borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.5 : 0.85) }} />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>User</TableCell>
                    <TableCell sx={tableHeadCellSx}>Channel</TableCell>
                    <TableCell sx={tableHeadCellSx}>Flow</TableCell>
                    <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    <TableCell sx={tableHeadCellSx}>Started</TableCell>
                    <TableCell sx={tableHeadCellSx}>Last Activity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.recentSessions?.length ? analytics.recentSessions.map((session) => (
                    <TableRow key={session.sessionKey}>
                      <TableCell>{session.userId || 'guest'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{session.channel}</TableCell>
                      <TableCell>{session.flowName}</TableCell>
                      <TableCell>{session.ended ? 'Completed' : 'In progress'}</TableCell>
                      <TableCell>{new Date(session.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{new Date(session.updatedAt).toLocaleString()}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={6}>No session activity found yet.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
